import { Navigate } from "react-router-dom";
import PageTitle from "../page-header/PageHeader";
import AddPIP from "./AddPIP";
import GetAllPIPs from "./GetAllPIPs";
import { Col, Row, Form } from "antd";
import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import { loadAllStaff } from "../../redux/rtk/features/user/userSlice";

const PIP = () => {
	const [form] = Form.useForm();
	const dispatch = useDispatch();
	const isLogged = Boolean(localStorage.getItem("isLogged"));
	const staffList = useSelector((state) => state.users?.list || []);

	const [pipList, setPipList] = useState(() => {
		// Try to load from localStorage first
		const storedPIPs = localStorage.getItem("performanceImprovementPlans");
		if (storedPIPs) {
			try {
				return JSON.parse(storedPIPs);
			} catch (error) {
				console.error("Error parsing PIPs from localStorage:", error);
			}
		}
		// Default dummy data
		const now = dayjs();
		return [
			{
				id: 1,
				employeeId: 3,
				employeeName: "Priya Verma",
				managerId: 1,
				managerName: "Manager A",
				startDate: now.subtract(30, "day").format("YYYY-MM-DD"),
				endDate: now.add(60, "day").format("YYYY-MM-DD"),
				reviewDate: now.add(15, "day").format("YYYY-MM-DD"),
				performanceConcerns: "Consistently missing project deadlines, low quality of deliverables, requires frequent supervision and guidance. Performance rating below expectations in last two review cycles.",
				rootCause: "Lack of time management skills, insufficient technical knowledge, limited problem-solving abilities, and lack of initiative in seeking help when stuck.",
				improvementObjectives: "1. Complete all assigned tasks within deadlines. 2. Improve code quality and reduce bugs by 50%. 3. Work independently on assigned projects. 4. Improve technical skills through training.",
				successCriteria: "1. Deliver 95% of tasks on time. 2. Code review feedback shows improvement. 3. Complete 2 projects independently. 4. Complete technical certification course.",
				actionItems: "1. Attend time management workshop. 2. Complete online technical training course. 3. Schedule weekly 1-on-1 meetings with manager. 4. Work on assigned projects with reduced supervision.",
				supportResources: "Manager will provide weekly mentoring sessions. Company will provide access to online training platform. Team lead will provide technical guidance when needed.",
				milestones: "Week 2: Complete time management training. Week 4: Complete first independent project. Week 6: Complete technical certification. Week 8: Final review and assessment.",
				managerComments: "We believe Priya has the potential to improve. Regular check-ins and support will be provided throughout the PIP period.",
				employeeComments: "I acknowledge the performance concerns and commit to working on the improvement plan. I appreciate the support being provided.",
				status: "Active",
			},
			{
				id: 2,
				employeeId: 5,
				employeeName: "Sara Smith",
				managerId: 1,
				managerName: "Manager A",
				startDate: now.subtract(15, "day").format("YYYY-MM-DD"),
				endDate: now.add(75, "day").format("YYYY-MM-DD"),
				reviewDate: now.add(20, "day").format("YYYY-MM-DD"),
				performanceConcerns: "Poor attendance, lack of engagement in team meetings, minimal contribution to projects, and failure to meet basic job requirements.",
				rootCause: "Lack of motivation, insufficient training, unclear expectations, and possible personal issues affecting work performance.",
				improvementObjectives: "1. Improve attendance to 95%. 2. Actively participate in team meetings. 3. Complete assigned tasks with acceptable quality. 4. Improve communication with team.",
				successCriteria: "1. Maintain 95% attendance rate. 2. Contribute meaningfully in all team meetings. 3. Complete all assigned tasks on time. 4. Receive positive feedback from team members.",
				actionItems: "1. Attend all scheduled meetings. 2. Complete basic job training program. 3. Set up daily check-ins with manager. 4. Work on improving communication skills.",
				supportResources: "HR will provide counseling support. Manager will provide daily guidance. Access to employee assistance program if needed.",
				milestones: "Week 1: Complete attendance improvement plan. Week 3: Complete basic training. Week 6: Mid-term review. Week 12: Final assessment.",
				managerComments: "This is a critical period. We need to see significant improvement in attendance and engagement. Support will be provided but expectations must be met.",
				employeeComments: "I understand the concerns and will work on improving my performance. I will attend all required training and meetings.",
				status: "Active",
			},
			{
				id: 3,
				employeeId: 2,
				employeeName: "Anshuman Singh",
				managerId: 2,
				managerName: "Manager B",
				startDate: now.subtract(90, "day").format("YYYY-MM-DD"),
				endDate: now.subtract(10, "day").format("YYYY-MM-DD"),
				reviewDate: now.subtract(5, "day").format("YYYY-MM-DD"),
				performanceConcerns: "Technical skills below expectations, difficulty in understanding complex requirements, slow learning curve, and inability to work independently.",
				rootCause: "Gap in technical knowledge, lack of hands-on experience, and need for more structured learning approach.",
				improvementObjectives: "1. Improve technical skills to meet job requirements. 2. Complete projects with minimal supervision. 3. Improve problem-solving abilities. 4. Enhance code quality.",
				successCriteria: "1. Complete technical certification. 2. Successfully complete 3 projects independently. 3. Code quality meets team standards. 4. Positive feedback from team lead.",
				actionItems: "1. Complete comprehensive technical training program. 2. Work on practice projects. 3. Attend code review sessions. 4. Pair programming with senior developers.",
				supportResources: "Access to technical training platform, mentorship from senior developers, additional time for learning and practice.",
				milestones: "Week 4: Complete technical fundamentals. Week 8: Complete first independent project. Week 10: Mid-term review. Week 12: Final assessment.",
				managerComments: "Anshuman showed good progress throughout the PIP. Technical skills improved significantly. PIP completed successfully.",
				employeeComments: "Thank you for the support and guidance. The training and mentorship helped me improve significantly.",
				status: "Completed",
			},
			{
				id: 4,
				employeeId: 4,
				employeeName: "Rahul Das",
				managerId: 2,
				managerName: "Manager B",
				startDate: now.subtract(45, "day").format("YYYY-MM-DD"),
				endDate: now.add(45, "day").format("YYYY-MM-DD"),
				reviewDate: now.add(10, "day").format("YYYY-MM-DD"),
				performanceConcerns: "Work-life balance issues affecting performance, taking on too many tasks leading to burnout, and declining quality of work.",
				rootCause: "Poor time management, inability to say no, perfectionist tendencies, and lack of delegation skills.",
				improvementObjectives: "1. Improve work-life balance. 2. Learn to prioritize tasks effectively. 3. Delegate when appropriate. 4. Maintain quality while managing workload.",
				successCriteria: "1. Work reasonable hours (not exceeding 45 hours/week). 2. Complete high-priority tasks on time. 3. Delegate appropriate tasks. 4. Maintain quality standards.",
				actionItems: "1. Attend time management and prioritization workshop. 2. Set clear boundaries for work hours. 3. Learn delegation techniques. 4. Regular check-ins with manager.",
				supportResources: "Access to work-life balance resources, time management training, coaching sessions with HR.",
				milestones: "Week 2: Complete time management training. Week 4: Implement work-hour boundaries. Week 6: Mid-term review. Week 12: Final assessment.",
				managerComments: "Rahul is a valuable team member. The focus is on helping him manage workload better and maintain work-life balance.",
				employeeComments: "I recognize the need to improve work-life balance. I will work on better time management and delegation.",
				status: "Under Review",
			},
			{
				id: 5,
				employeeId: 1,
				employeeName: "Pradeep Kumar",
				managerId: 2,
				managerName: "Manager B",
				startDate: now.subtract(120, "day").format("YYYY-MM-DD"),
				endDate: now.subtract(30, "day").format("YYYY-MM-DD"),
				reviewDate: now.subtract(25, "day").format("YYYY-MM-DD"),
				performanceConcerns: "Inconsistent performance, failure to meet improvement goals, lack of commitment to PIP, and continued performance issues.",
				rootCause: "Lack of engagement, unwillingness to improve, and failure to take PIP seriously.",
				improvementObjectives: "1. Meet all improvement goals. 2. Show consistent performance. 3. Demonstrate commitment to improvement. 4. Meet all job requirements.",
				successCriteria: "1. Complete all PIP objectives. 2. Maintain consistent performance. 3. Positive feedback from manager. 4. Meet job expectations.",
				actionItems: "1. Attend all required training. 2. Complete all assigned tasks. 3. Regular check-ins with manager. 4. Demonstrate improvement.",
				supportResources: "Manager support, training resources, and regular feedback sessions.",
				milestones: "Week 4: First review. Week 8: Mid-term review. Week 12: Final assessment.",
				managerComments: "Despite support and resources provided, the employee failed to meet improvement goals. PIP terminated due to lack of progress.",
				employeeComments: "I acknowledge the termination of the PIP.",
				status: "Terminated",
			},
		];
	});

	useEffect(() => {
		dispatch(loadAllStaff({ status: true }));
	}, [dispatch]);

	const mapEmployeeName = (employeeId) => {
		const employee = staffList.find((e) => e.id === employeeId);
		if (employee) {
			return (
				employee.EmployeeName ||
				`${employee.firstName || ""} ${employee.lastName || ""}`.trim() ||
				employee.UserName
			);
		}
		return "Unknown";
	};

	const handleSubmit = (values) => {
		const newId = pipList.length ? Math.max(...pipList.map((p) => p.id || 0)) + 1 : 1;
		const payload = {
			id: newId,
			employeeId: values.employeeId,
			managerId: values.managerId,
			employeeName: mapEmployeeName(values.employeeId),
			managerName: mapEmployeeName(values.managerId),
			startDate: values.startDate ? dayjs(values.startDate).format("YYYY-MM-DD") : null,
			endDate: values.endDate ? dayjs(values.endDate).format("YYYY-MM-DD") : null,
			reviewDate: values.reviewDate ? dayjs(values.reviewDate).format("YYYY-MM-DD") : null,
			performanceConcerns: values.performanceConcerns || "",
			rootCause: values.rootCause || "",
			improvementObjectives: values.improvementObjectives || "",
			successCriteria: values.successCriteria || "",
			actionItems: values.actionItems || "",
			supportResources: values.supportResources || "",
			milestones: values.milestones || "",
			managerComments: values.managerComments || "",
			employeeComments: values.employeeComments || "",
			status: values.status || "Draft",
		};
		setPipList((prev) => [...prev, payload]);
		form.resetFields();
	};

	const handleEdit = (record) => {
		form.setFieldsValue({
			employeeId: record.employeeId,
			managerId: record.managerId,
			startDate: record.startDate ? dayjs(record.startDate) : null,
			endDate: record.endDate ? dayjs(record.endDate) : null,
			reviewDate: record.reviewDate ? dayjs(record.reviewDate) : null,
			performanceConcerns: record.performanceConcerns,
			rootCause: record.rootCause,
			improvementObjectives: record.improvementObjectives,
			successCriteria: record.successCriteria,
			actionItems: record.actionItems,
			supportResources: record.supportResources,
			milestones: record.milestones,
			managerComments: record.managerComments,
			employeeComments: record.employeeComments,
			status: record.status,
		});
	};

	const refresh = () => {
		setPipList((prev) => [...prev]);
	};

	// Sync PIPs to localStorage whenever it changes
	useEffect(() => {
		localStorage.setItem("performanceImprovementPlans", JSON.stringify(pipList));
	}, [pipList]);

	if (!isLogged) {
		return <Navigate to={"/admin/auth/login"} replace={true} />;
	}

	return (
		<div className="add-pip-page-wrapper">
			<PageTitle title="Back" />
			<Row gutter={16} className="add-pip-row">
				<Col xs={24} sm={24} md={10} lg={8} className="add-pip-form-col">
					<AddPIP form={form} onSubmit={handleSubmit} />
				</Col>
				<Col xs={24} sm={24} md={14} lg={16} className="add-pip-table-col">
					<GetAllPIPs data={pipList} onEdit={handleEdit} onRefresh={refresh} />
				</Col>
			</Row>
		</div>
	);
};

export default PIP;

